/**
 * Adds a visible, keyboard-operable pause/play control over every looping
 * animated GIF image on the page (WCAG 2.2.2 - Pause, Stop, Hide). A native
 * GIF cannot be paused directly, so this freezes it by drawing the
 * current frame to a canvas and swapping visibility; "play" restores the
 * live, looping GIF.
 *
 * Every GIF is wrapped in a dedicated span sized to exactly match the image,
 * so the pause button always overlays the image itself regardless of what
 * the image's original parent looks like. If the image is already inside a
 * link or button (e.g. a small icon inside a clickable tile), no separate
 * button element is added at all - nesting a button inside a link/button is
 * invalid, and the click wouldn't be isolated from the parent's own click
 * either. prefers-reduced-motion still freezes the GIF automatically in
 * that case.
 *
 * Runs on load, on Shopify section reloads, and via a MutationObserver so
 * GIFs injected later by Nosto's client-side PLP rendering are also caught.
 */
(function () {
  var PAUSE_ICON =
    '<svg aria-hidden="true" focusable="false" viewBox="0 0 24 24"><path d="M7 5a1 1 0 0 0-1 1v12a1 1 0 0 0 1 1h2a1 1 0 0 0 1-1V6a1 1 0 0 0-1-1H7zm8 0a1 1 0 0 0-1 1v12a1 1 0 0 0 1 1h2a1 1 0 0 0 1-1V6a1 1 0 0 0-1-1h-2z" fill="currentColor"/></svg>';
  var PLAY_ICON =
    '<svg aria-hidden="true" focusable="false" viewBox="0 0 24 24"><path d="M6.906 4.537A.6.6 0 0 0 6 5.053v13.894a.6.6 0 0 0 .906.516l11.723-6.947a.6.6 0 0 0 0-1.032L6.906 4.537z" fill="currentColor"/></svg>';

  var PAUSE_LABEL = "Pause animation";
  var PLAY_LABEL = "Play animation";

  var reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var enhanced = new WeakSet();

  function isGif(img) {
    return /\.gif(\?|$)/i.test(img.currentSrc || img.src || '');
  }

  function enhance(img) {
    if (enhanced.has(img)) return;
    enhanced.add(img);

    var originalSrc = img.currentSrc || img.src;
    var insideClickableAncestor = !!img.closest('a, button, [role="button"]');

    var wrapper = document.createElement('span');
    wrapper.className = 'gif-pause-toggle__wrapper';
    img.parentNode.insertBefore(wrapper, img);
    wrapper.appendChild(img);

    var canvas = document.createElement('canvas');
    canvas.className = 'gif-pause-toggle__frame';
    canvas.setAttribute('aria-hidden', 'true');
    canvas.hidden = true;
    wrapper.appendChild(canvas);

    var button = null;
    if (!insideClickableAncestor) {
      button = document.createElement('button');
      button.type = 'button';
      button.className = 'gif-pause-toggle';
      button.setAttribute('aria-pressed', 'false');
      button.setAttribute('aria-label', PAUSE_LABEL);
      button.innerHTML =
        '<span class="gif-pause-toggle__icon" data-icon-pause>' + PAUSE_ICON + '</span>' +
        '<span class="gif-pause-toggle__icon" data-icon-play hidden>' + PLAY_ICON + '</span>';
      wrapper.appendChild(button);
    }

    var paused = false;

    function freeze() {
      if (!img.naturalWidth) return false;
      canvas.width = img.naturalWidth;
      canvas.height = img.naturalHeight;
      canvas.style.objectFit = window.getComputedStyle(img).objectFit;

      var ctx = canvas.getContext('2d');
      try {
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      } catch (error) {
        return false;
      }
      return true;
    }

    function setPaused(next) {
      if (next) {
        if (!freeze()) return;
        canvas.hidden = false;
        img.style.visibility = 'hidden';
      } else {
        canvas.hidden = true;
        img.style.visibility = '';
        // Restart the GIF loop from frame 0.
        img.src = '';
        img.src = originalSrc;
      }

      paused = next;

      if (button) {
        button.setAttribute('aria-pressed', paused ? 'true' : 'false');
        button.setAttribute('aria-label', paused ? PLAY_LABEL : PAUSE_LABEL);

        var pauseIcon = button.querySelector('[data-icon-pause]');
        var playIcon = button.querySelector('[data-icon-play]');
        if (pauseIcon) pauseIcon.hidden = paused;
        if (playIcon) playIcon.hidden = !paused;
      }
    }

    if (button) {
      button.addEventListener('click', function (event) {
        // Isolate this control's click from any clickable ancestor,
        // belt-and-suspenders in case this ever ends up nested again.
        event.stopPropagation();
        setPaused(!paused);
      });
    }

    if (reducedMotion) {
      if (img.complete) setPaused(true);
      else img.addEventListener('load', function () { setPaused(true); }, { once: true });
    }
  }

  function sweep(root) {
    (root || document).querySelectorAll('img').forEach(function (img) {
      if (isGif(img)) enhance(img);
    });
  }

  document.addEventListener('DOMContentLoaded', function () {
    sweep(document);
  });
  document.addEventListener('shopify:section:load', function (evt) {
    sweep(evt.target);
  });

  if (typeof MutationObserver !== 'undefined') {
    new MutationObserver(function (mutations) {
      mutations.forEach(function (mutation) {
        mutation.addedNodes.forEach(function (node) {
          if (node.nodeType !== 1) return;
          if (node.tagName === 'IMG') {
            if (isGif(node)) enhance(node);
          } else if (node.querySelectorAll) {
            sweep(node);
          }
        });
      });
    }).observe(document.body || document.documentElement, {
      childList: true,
      subtree: true,
    });
  }
})();
